import { Component, OnInit, OnDestroy, ViewEncapsulation, DestroyRef, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { GroupUserService } from '@core/services/group-user.service';
import { CommonFunctions } from '@shared/common-functions';
import { ToastService } from '@shared/services/toast.service';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { CancelBookingType, ModalData } from '@core/models/api.response.modal';
import { Subject, takeUntil } from 'rxjs';
import { CustomValidator } from '@shared/services/custom-validatior.abstract';
import { SuperAdminService } from '@core/services/super-admin.service';
import { autoUnsubWith } from '@shared/services/utility.abstract';

@Component({
  selector: 'app-group-cancel-booking-modal',
  templateUrl: './group-cancel-booking-modal.component.html',
  styleUrls: ['./group-cancel-booking-modal.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class GroupCancelBookingModalComponent implements OnInit, OnDestroy {
  readonly destroyRef = inject(DestroyRef);
  CancelBookingType = CancelBookingType;
  cancelBookingForm!: FormGroup;
  fetchModalData!: ModalData;
  totalAmount: number;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly ref: DynamicDialogRef,
    private readonly groupService: GroupUserService,
    private readonly toastService: ToastService,
    private readonly config: DynamicDialogConfig,
    private readonly fb: FormBuilder,
    private readonly superAdminServie:SuperAdminService
  ) { }

  ngOnInit(): void {
    this.fetchModalData = this.config.data;
    this.totalAmount = this.fetchModalData?.total;
    this.initializeForm();
    this.setupFormListeners();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeForm(): void {
    this.cancelBookingForm = this.fb.group({
      bookingType: [CancelBookingType.FullCancelBooking, Validators.required],
      numberOfSpots: [
        '',
        [
          Validators.required,
          CustomValidator.validateGreaterThanZero,
          CustomValidator.lessThanSlotCountValidator(this.fetchModalData?.spotCount),
        ],
      ],
    });
  }

  private setupFormListeners(): void {
    this.cancelBookingForm
      .get('bookingType')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value: CancelBookingType) => {
        if (value === CancelBookingType.FullCancelBooking) {
          this.cancelBookingForm.get('numberOfSpots')?.reset();
        }
      });
  }

  confirm(): void {
    const { bookingType } = this.cancelBookingForm.value;
    if (this.cancelBookingForm.invalid && bookingType !== 2) {
      this.cancelBookingForm.markAllAsTouched();
      return;
    }
    const request = {
      bookingId: this.fetchModalData.id,
      cancelBookingType: CancelBookingType.FullCancelBooking,
      slotCount: null,
    };

    this.groupService.cancelOneTimeBookingBySuperAdmin(request)
    .pipe(autoUnsubWith(this.destroyRef))
    .subscribe({
      next: ({ message }) => this.handleResponse('success', message),
      error: (error) => this.handleResponse('error', error.error?.message),
    });
  }

  cancelRecurringBooking(): void {
    const { bookingType } = this.cancelBookingForm.value;
    if (this.cancelBookingForm.invalid && bookingType !== 2) {
      this.cancelBookingForm.markAllAsTouched();
      return;
    }

    const request = {
      bookingId: this.fetchModalData.id,
      cancelBookingType: bookingType,
      slotCount: null,
      isOnlySubscriptionCancel: false 
    };

    this.superAdminServie.cancelRecurringBooking(request)
    .pipe(autoUnsubWith(this.destroyRef))
    .subscribe({
      next: ({ message }) => this.handleResponse('success', message),
      error: (error) => this.handleResponse('error', error.error?.message),
    });
  }

  private handleResponse(status: 'success' | 'error', message: string): void {
    this.toastService.showToastMessage(status, status === 'success' ? 'Success' : 'Error', message);

    if (status === 'success') {
      setTimeout(() => {
        this.ref.close(status);
      }, 1000);
    }
  }

  closeDialog(): void {
    this.ref.close();
  }

  limitInputLength(): void {
    const control = this.cancelBookingForm.get('numberOfSpots');
    if (control?.value.length > 2) {
      control.setValue(control.value.slice(0, 2));
    }
  }

  onKeyPress(event: KeyboardEvent): void {
    CommonFunctions.onlyAcceptNumber(event);
  }
}